/**
 * Keyboard Shortcuts Modal
 *
 * Displays all available keyboard shortcuts organized by category.
 * Can be opened with the "?" key.
 */

import * as React from 'react';
import { Modal } from './common/Modal';
import { type Shortcut, getShortcutDisplay, isMac } from '@/shared/hooks/useKeyboardShortcuts';

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
  shortcuts: Record<string, Shortcut>;
}

// Category labels and order
const categoryConfig: Record<string, { label: string; icon: string; order: number }> = {
  global: { label: 'Global', icon: '🌐', order: 1 },
  navigation: { label: 'Navigation', icon: '🧭', order: 2 },
  actions: { label: 'Actions', icon: '⚡', order: 3 },
  editing: { label: 'Editing', icon: '✏️', order: 4 },
};

const ShortcutKey: React.FC<{ children: string }> = ({ children }) => (
  <kbd className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 text-sm font-mono font-medium bg-white/10 border border-white/20 rounded-md text-slate-200 shadow-sm">
    {children}
  </kbd>
);

const ShortcutRow: React.FC<{ shortcut: Shortcut }> = ({ shortcut }) => {
  const displayKey = getShortcutDisplay(shortcut);
  const parts = displayKey.split(isMac ? '' : '+');

  return (
    <div className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-white/5 transition-colors">
      <span className="text-slate-300 text-sm">{shortcut.description}</span>
      <div className="flex items-center gap-1 ml-4">
        {isMac ? (
          <ShortcutKey>{displayKey}</ShortcutKey>
        ) : (
          parts.map((part, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className="text-slate-500 text-xs mx-0.5">+</span>}
              <ShortcutKey>{part}</ShortcutKey>
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
};

const CategorySection: React.FC<{
  category: string;
  shortcuts: Shortcut[];
}> = ({ category, shortcuts }) => {
  const config = categoryConfig[category] || { label: category, icon: '📋', order: 99 };

  return (
    <div className="mb-6 last:mb-0">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
        <span className="text-lg">{config.icon}</span>
        <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
          {config.label}
        </h3>
      </div>
      <div className="space-y-1">
        {shortcuts.map((shortcut, index) => (
          <ShortcutRow key={index} shortcut={shortcut} />
        ))}
      </div>
    </div>
  );
};

export const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({
  isOpen,
  onClose,
  shortcuts,
}) => {
  // Group shortcuts by category
  const groupedShortcuts = React.useMemo(() => {
    const groups: Record<string, Shortcut[]> = {};

    for (const shortcut of Object.values(shortcuts)) {
      const category = shortcut.category || 'global';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(shortcut);
    }

    // Sort categories by order
    const sortedCategories = Object.keys(groups).sort((a, b) => {
      const orderA = categoryConfig[a]?.order ?? 99;
      const orderB = categoryConfig[b]?.order ?? 99;
      return orderA - orderB;
    });

    return sortedCategories.map(category => ({
      category,
      shortcuts: groups[category],
    }));
  }, [shortcuts]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Keyboard Shortcuts">
      <div className="max-w-2xl mx-auto">
        {/* Platform hint */}
        <p className="text-slate-400 text-sm mb-6 text-center">
          {isMac ? (
            <>Using <span className="text-white font-medium">⌘ Command</span> for shortcuts</>
          ) : (
            <>Using <span className="text-white font-medium">Ctrl</span> for shortcuts</>
          )}
        </p>

        {/* Shortcuts by category */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {groupedShortcuts.map(({ category, shortcuts }) => (
            <CategorySection key={category} category={category} shortcuts={shortcuts} />
          ))}
        </div>

        {/* Tip */}
        <div className="mt-8 pt-6 border-t border-white/10">
          <div className="flex items-start gap-3 text-sm text-slate-400">
            <span className="text-cyan-400 text-lg">💡</span>
            <p>
              Shortcuts are disabled when typing in text fields. Press{' '}
              <ShortcutKey>Esc</ShortcutKey> to close this modal or any open panel.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default KeyboardShortcutsModal;
